import { Avatar, Divider, Flex, List, Modal } from 'antd';

import { User } from '@Types/index';
import { ApprovalHistory as ApprovalHistoryItem } from '@Types/api/requestList';
import { ReactComponent as AvatarPlaceholder } from '@Icons/additional/avatar-user.svg';
import { parseDateTime } from '@Utils/transformDateTime';
import { useGetEmpPhotoQuery } from '@Store/api/dictionaryApiSlice';

import styles from './approval-history.module.css';
import { getDescription, getModalTitle } from './utils';
import { DATE_FORMAT, REMARKS_PLACEHOLDER } from './constants';

interface ActionCardProps {
  photo: string | null;
  name: string;
  action: string;
  occupation: string;
  remarks: string;
  date: string;
}

const ActionCard: React.FC<ActionCardProps> = ({
  photo,
  name,
  action,
  occupation,
  remarks,
  date: dateRaw,
}) => {
  const description = getDescription(action, occupation);
  const date = parseDateTime(dateRaw, DATE_FORMAT);
  const { data: userPhoto } = useGetEmpPhotoQuery(photo || '');

  const leftBlock = (
    <Flex className={styles.leftBlock}>
      <Avatar
        className={styles.avatar}
        size={40}
        icon={<AvatarPlaceholder />}
        src={userPhoto}
      />
      <Flex vertical>
        <span className={styles.name}>{name}</span>
        <span className={styles.description}>{description}</span>
      </Flex>
    </Flex>
  );
  const rightBlock = <span className={styles.date}>{date}</span>;
  const mainBlock = (
    <Flex className={styles.mainBlock}>
      {leftBlock}
      {rightBlock}
    </Flex>
  );

  return (
    <div className={styles.card}>
      {mainBlock}
      <Divider className={styles.cardDivider} />
      <div className={styles.remarks}>{remarks || REMARKS_PLACEHOLDER}</div>
    </div>
  );
};

interface ModalContentProps {
  users: User[];
  onClose: () => void;
  data: ApprovalHistoryItem[] | null;
}

const ModalContent: React.FC<ModalContentProps> = ({ data, users }) => {
  if (!data) {
    return null;
  }

  return (
    <List
      dataSource={data}
      itemLayout="vertical"
      renderItem={(item) => {
        let matchedUser;

        if (item?.actionByEmpNumber) {
          matchedUser = users.find(
            (userItem) => userItem.empNumber === item.actionByEmpNumber
          );
        } else {
          matchedUser = users.find(
            (userItem) => userItem.empNumber === item.actionBy
          );
        }

        return (
          <ActionCard
            photo={matchedUser?.empImageName || ''}
            name={matchedUser?.empName || ''}
            occupation={matchedUser?.occupation || ''}
            action={item.action}
            remarks={item.comments}
            date={item.actionDate}
          />
        );
      }}
    />
  );
};

interface ApprovalHistoryProps {
  isOpen: boolean;
  users: User[];
  onClose: () => void;
  data: ApprovalHistoryItem[] | null;
  requestNo?: string | number;
}

const ApprovalHistory: React.FC<ApprovalHistoryProps> = (props) => {
  const { isOpen, onClose, users, data, requestNo } = props;

  // requestId should be the same in all data items, so we may take first one
  const requestId = data?.[0]?.requestId;

  const title = getModalTitle(requestId || Number(requestNo));

  return (
    <Modal
      title={title}
      open={isOpen}
      footer={null}
      onCancel={onClose}
      width={600}
      className={styles.modal}
      closable
    >
      <Divider className={styles.titleDivider} />
      <ModalContent data={data} users={users} onClose={onClose} />
    </Modal>
  );
};

export default ApprovalHistory;
